/**
 * IMPORTANT - BLOCKCHAIN DATA INTEGRITY:
 * This file handles blockchain data which must maintain exact values and case sensitivity.
 * - NEVER use toLowerCase() or toUpperCase()
 * - NEVER normalize strings or addresses
 * - NEVER transform collection names
 * - NEVER modify contract addresses
 * - ALWAYS use exact matches for comparisons
 * - ALWAYS preserve original data as it exists on-chain
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import {
  AppBar,
  Box,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Toolbar,
  Typography,
  Paper,
  CircularProgress,
  FormControlLabel,
  ListSubheader
} from '@mui/material';
import ReactSelect from 'react-select';
import { fetchCollections, fetchLoans } from './api/nftfi';
import { fetchCollectionId, fetchFloorPrice } from './api/reservoir';
import { fetchHistoricalFloorPrices } from './api/pricefloor';
import { theme } from './theme';
import { TOP_COLLECTIONS } from './shared/constants';

function App() {
  const [collections, setCollections] = useState([]);
  const [topCollections, setTopCollections] = useState([]);
  const [otherCollections, setOtherCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [cumulative, setCumulative] = useState(true);
  
  // Data states
  const [loans, setLoans] = useState(null);
  const [floorPrice, setFloorPrice] = useState({ usd: 0, eth: 0 });
  const [historicalPrices, setHistoricalPrices] = useState(null);
  const [chartData, setChartData] = useState(null);

  // Add these state variables in the App function
  const [collectionVolumes, setCollectionVolumes] = useState({});
  const [totalLoanVolume, setTotalLoanVolume] = useState(0);

  const loadCollections = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Loading collections...');

      const collectionsData = await fetchCollections();

      if (collectionsData && collectionsData.length > 0) {
        // Extract collection names
        const collectionNames = collectionsData.map(c => c.nftProjectName);

        // Predefined top collections - exact on-chain names
        const topCollectionsList = [
          'CryptoPunks',
          'Bored Ape Yacht Club',
          'Mutant Ape Yacht Club',
          'Azuki',
          'Moonbirds',
          'CLONE X',
          'Doodles',
          'Cool Cats',
          'Wrapped Cryptopunks',
          'World of Women',
          'Pudgy Penguins',
          'VeeFriends',
          'Meebits',
          'RENGA',
          'Milady',
          'mfers'
        ];

        // Filter top collections that exist in collectionsData - exact matches only
        const top16 = topCollectionsList.filter(c => collectionNames.includes(c));
        
        // Get remaining collections and sort alphabetically
        const rest = collectionNames
          .filter(c => !top16.includes(c))
          .sort((a, b) => a.localeCompare(b));

        setCollections(collectionNames);
        setTopCollections(top16);
        setOtherCollections(rest);
      }
    } catch (error) {
      console.error('Error loading collections:', error);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // This function loads all data needed for the chart in a single batch
  const loadCollectionData = useCallback(async (collection) => {
    if (!collection) return;
    
    // Reset chart data while loading
    setChartData(null);
    setLoading(true);
    setLoadingMessage('Loading loans...');

    try {
      // First, fetch all the required data
      // 1. Fetch loans
      const loansData = await fetchLoans(collection, 365, (count) => {
        setLoadingMessage(`Loading loans (${count} found)`);
      });
      
      // 2. Get collection address
      setLoadingMessage('Fetching collection details...');
      const collectionId = await fetchCollectionId(collection);
      
      // 3. Fetch floor price
      setLoadingMessage('Fetching floor price...');
      const floorPriceData = await fetchFloorPrice(collectionId);
      
      // 4. Fetch historical floor prices
      setLoadingMessage('Fetching historical floor prices...');
      let historicalPricesData = { usd: new Map(), native: new Map() };
      try {
        historicalPricesData = await fetchHistoricalFloorPrices(collection);
      } catch (error) {
        console.error('[Error] Failed to fetch historical prices:', error);
      }
      
      // Only after all data is loaded, update the state in one batch
      setLoans(loansData);
      setFloorPrice(floorPriceData || { usd: 0, eth: 0 });
      setHistoricalPrices(historicalPricesData);
      
      // Finally, set the combined chart data to trigger a single render
      setChartData({
        collection,
        loans: loansData,
        floorPrice: floorPriceData || { usd: 0, eth: 0 },
        historicalPrices: historicalPricesData
      });
      
    } catch (error) {
      console.error('Error loading collection data:', error);
      setChartData(null);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, []);

  // Convert collections to the format expected by react-select with grouping
  const collectionOptions = useMemo(() => {
    // Early return if collections array is empty
    if (!collections.length) return [];
    
    // Find top collections that are available in our data - use exact matches only
    const topCollectionsAvailable = TOP_COLLECTIONS
      .map(name => collections.find(c => c.nftProjectName === name))
      .filter(Boolean);
    
    // Get the remaining collections (excluding top ones)
    const topCollectionNames = topCollectionsAvailable.map(c => c.nftProjectName);
    const remainingCollections = collections.filter(c => 
      !topCollectionNames.includes(c.nftProjectName)
    );
    
    // Create grouped options with both name and contract address
    return [
      {
        label: '⭐ Top Collections',
        options: topCollectionsAvailable.map(collection => ({
          value: collection.contractAddress || collection.nftProjectName,
          label: collection.nftProjectName,
          isAddress: Boolean(collection.contractAddress)
        }))
      },
      {
        label: 'All Collections',
        options: remainingCollections.map(collection => ({
          value: collection.contractAddress || collection.nftProjectName,
          label: collection.nftProjectName,
          isAddress: Boolean(collection.contractAddress)
        }))
      }
    ];
  }, [collections]);

  const handleCollectionChange = (selectedOption) => {
    if (!selectedOption) {
      setSelectedCollection(null);
      return;
    }
    
    const collection = selectedOption.value;
    setSelectedCollection(collection);
    loadCollectionData(collection);
  };

  // Initialize app
  useEffect(() => {
    const init = async () => {
      try {
        console.log('Initializing app...');
        await loadCollections();
        console.log('Collections loaded successfully');
      } catch (error) {
        console.error('Error during initialization:', error);
      }
    };
    init();
  }, []);

  // Check if we have complete data for the chart
  const hasCompleteData = chartData && 
    chartData.loans && 
    chartData.floorPrice && 
    chartData.historicalPrices;

  // Custom styles for react-select
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minWidth: 400,
      width: '100%',
      backgroundColor: 'white',
      borderColor: '#ccc',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      maxHeight: '80vh',
      overflow: 'auto',
      width: '100%'
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '80vh'
    }),
    option: (provided, state) => ({
      ...provided,
      color: 'black',
      backgroundColor: state.isSelected ? '#1976d2' : state.isFocused ? '#f0f0f0' : 'white',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'black',
    }),
    input: (provided) => ({
      ...provided,
      color: 'black',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#757575',
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      color: '#666',
    }),
    group: (provided) => ({
      ...provided,
      paddingBottom: 8,
    }),
    groupHeading: (provided) => ({
      ...provided,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#1976d2',
      backgroundColor: '#f5f5f5',
      margin: 0,
      padding: '8px 12px',
      borderBottom: '1px solid #ddd',
    })
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static" sx={{ backgroundColor: 'background.paper', boxShadow: 1 }}>
          <Toolbar>
            <Box sx={{ mr: 2, minWidth: 400, width: 400 }}>
              <FormControl fullWidth variant="outlined" size="small" disabled={loading}>
                <InputLabel id="collection-select-label">Select Collection</InputLabel>
                <Select
                  labelId="collection-select-label"
                  id="collection-select"
                  value={selectedCollection || ""}
                  onChange={(e) => {
                    const collection = e.target.value;
                    if (collection) {
                      setSelectedCollection(collection);
                      loadCollectionData(collection);
                    } else {
                      setSelectedCollection(null);
                    }
                  }}
                  label="Select Collection"
                >
                  <ListSubheader>⭐ Top Collections</ListSubheader>
                  {topCollections.map((collection) => (
                    <MenuItem key={collection} value={collection}>
                      {collection}
                    </MenuItem>
                  ))}
                  <ListSubheader>Other Collections</ListSubheader>
                  {otherCollections.map((collection) => (
                    <MenuItem key={collection} value={collection}>
                      {collection}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={cumulative}
                  onChange={(e) => setCumulative(e.target.checked)}
                  disabled={loading || !hasCompleteData}
                  color="primary"
                />
              }
              label="Cumulative"
              sx={{ 
                ml: 1,
                display: 'flex', 
                alignItems: 'center',
                '& .MuiTypography-root': { 
                  marginLeft: '4px',
                  fontWeight: 500,
                  color: 'text.primary'
                },
                visibility: 'visible',
                opacity: 1,
                pointerEvents: 'auto'
              }}
            />
          </Toolbar>
        </AppBar>

        <Box sx={{ position: 'relative', p: 3 }}>
          {/* Welcome message when no collection is selected */}
          {!selectedCollection && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                Welcome to the Depth Chart Basic
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Please select an NFT collection from the dropdown menu above to visualize loan-to-value data.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This tool helps you analyze the distribution of loans across different LTV ratios for NFT collections.
              </Typography>
            </Box>
          )}

          {/* Modal loading indicator */}
          {loading && (
            <Box 
              sx={{ 
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                padding: 4,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                minWidth: '250px',
                backdropFilter: 'blur(5px)',
                zIndex: 1000
              }}
            >
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{loadingMessage}</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App; 